import React, { useEffect, useState, type ReactNode } from 'react';

import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useNotification } from '@hooks/operator/useBackgroundJobNotification';
import { BackgroundJobProvider, useBackgroundJob } from '@shared/BackgroundJobManager';

import operatorApiFetch from '@services/operator/apiService';
import operatorCable from '@/lib/operatorCable';

/**
 * Operator Notification Context
 *
 * 用途：在整個 Operator 應用中共享即時通知和背景任務管理功能
 * 使用統一的 BackgroundJobProvider 架構
 */

// BackgroundJobManager 仍為 .jsx，TS 推不出 contextValue 形狀；
// 此處依其 runtime contract 顯式宣告 hook return shape，讓 callers 不必再 cast。
export interface BackgroundJob {
  id: string;
  name: string;
  status?: string;
  [key: string]: unknown;
}

export interface OperatorNotificationContextValue {
  jobs: BackgroundJob[];
  addJob: (jobId: string, jobName: string, optionsOrOnComplete?: unknown) => void;
  removeJob: (jobId: string) => void;
  clearCompletedJobs: () => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  jobCount: number;
  hasRunningJobs: boolean;
  hasFailedJobs: boolean;
  apiFetch: typeof operatorApiFetch;
  syncPendingJobs: () => Promise<void> | void;
  notificationData: Record<string, unknown>;
  registerJob: (...args: unknown[]) => void;
}

/**
 * OperatorNotificationProvider - Operator 專用的通知和背景任務 Provider
 *
 * 功能：
 * - 使用統一的 BackgroundJobProvider
 * - 注入 Operator 專用的 Notification Hook 和 API Service
 * - 自動同步未完成任務（當 operator 載入完成後）
 * - 提供即時通知資料（包含 job_status 和 new_notification）
 */
export function OperatorNotificationProvider({ children }: { children: ReactNode }) {
  return (
    <BackgroundJobProvider
      useNotificationHook={useNotification}
      apiFetch={operatorApiFetch}
      storageKey="operator_background_jobs"
      cableManager={operatorCable}
    >
      <OperatorNotificationSyncWrapper>
        {children}
      </OperatorNotificationSyncWrapper>
    </BackgroundJobProvider>
  );
}

/**
 * OperatorNotificationSyncWrapper - 負責同步未完成任務
 *
 * 這個內部組件可以存取 BackgroundJobProvider 的 Context
 */
function OperatorNotificationSyncWrapper({ children }: { children: ReactNode }) {
  const { syncPendingJobs } = useBackgroundJob() as OperatorNotificationContextValue;
  const { operator, loading: operatorLoading } = useOperatorAuth();
  const [synced, setSynced] = useState(false);

  // 🔥 等待 operator 載入完成後，同步未完成的任務狀態
  useEffect(() => {
    if (!operatorLoading && operator && !synced) {
      syncPendingJobs();
      setSynced(true);
    }
  }, [operatorLoading, operator, synced, syncPendingJobs]);

  return <>{children}</>;
}

/**
 * useOperatorNotification - 使用 Operator 即時通知和背景任務的 Hook
 *
 * 使用範例：
 * ```tsx
 * import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext';
 *
 * function MyComponent() {
 *   const { addJob, notificationData } = useOperatorNotification();
 *
 *   const handleExport = async () => {
 *     // ... API call
 *     addJob(job_id, 'Export Task');
 *   };
 * }
 * ```
 */
export function useOperatorNotification(): OperatorNotificationContextValue {
  return useBackgroundJob() as OperatorNotificationContextValue;
}
